import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Text } from 'v3/components';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

function getInputValue(arg) {
  if (arg === null || arg === undefined) return '';
  if (typeof arg === 'string' || typeof arg === 'number') return String(arg);
  if (arg?.target && 'value' in arg.target) return String(arg.target.value ?? '');
  if ('value' in arg) return String(arg.value ?? '');
  return String(arg ?? '');
}

function toArray(v) {
  return Array.isArray(v) ? v : v ? [v] : [];
}

function normalizeGroupFilter(groupFilter) {
  const arr = toArray(groupFilter).map(String).filter(Boolean);
  if (arr.includes('all')) return [];
  return arr;
}

export default function ListModal({
  models = [],
  groupTabs = [],
  groupFilter,
  onChangeGroupFilter,
  search,
  onChangeSearch,
  onSelectModel,
}) {
  const SEARCH_DEBOUNCE_MS = 1200;
  const [localSearch, setLocalSearch] = useState(search || '');
  const debounceRef = useRef(null);
  const initRef = useRef(true);

  useEffect(() => {
    setLocalSearch(search || '');
  }, [search]);

  useEffect(() => {
    if (initRef.current) {
      initRef.current = false;
      return;
    }

    if (!onChangeSearch) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      onChangeSearch(String(localSearch || ''));
      debounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localSearch, onChangeSearch]);

  const applyImmediateSearch = useCallback(
    (nextValue) => {
      if (!onChangeSearch) return;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      onChangeSearch(String(nextValue || ''));
    },
    [onChangeSearch]
  );
  const selectedGroups = useMemo(() => normalizeGroupFilter(groupFilter), [groupFilter]);
  const toggleGroup = useCallback(
    (key) => {
      if (!onChangeGroupFilter) return;

      if (key === 'all') {
        onChangeGroupFilter('all');
        return;
      }

      const current = normalizeGroupFilter(groupFilter);
      const exists = current.includes(String(key));
      const next = exists ? current.filter((x) => x !== String(key)) : [...current, String(key)];
      onChangeGroupFilter(next.length ? next : 'all');
    },
    [groupFilter, onChangeGroupFilter]
  );

  const filteredModels = useMemo(() => {
    const term = String(localSearch || '').trim().toLowerCase();
    const selected = selectedGroups;

    return (models || []).filter((model) => {
      if (selected.length) {
        const keys = Array.isArray(model?.groupsKeys) ? model.groupsKeys.map(String) : [];
        const matchesAny = selected.some((g) => keys.includes(g));
        if (!matchesAny) return false;
      }
      if (!term) return true;

      return (
        String(model?.title || '').toLowerCase().includes(term) ||
        String(model?.description || '').toLowerCase().includes(term) ||
        String(model?.group || '').toLowerCase().includes(term)
      );
    });
  }, [models, selectedGroups, localSearch]);

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            height: 44,
            borderRadius: 8,
            border: '2px solid #D1D5DB',
            padding: '0 12px',
            background: '#FFFFFF',
          }}
        >
          <SearchIcon style={{ fontSize: 18, color: '#9CA3AF' }} />

          <input
            type="text"
            placeholder="Buscar checklists..."
            value={localSearch || ''}
            onChange={(e) => setLocalSearch(getInputValue(e))}
            onKeyDown={(e) => {
              if (e?.key === 'Enter') {
                e.preventDefault();
                applyImmediateSearch(localSearch);
              }
            }}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              outline: 'none',
              fontSize: 14,
              color: '#111827',
              background: 'transparent',
            }}
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <TuneIcon style={{ fontSize: 18, color: '#B6BDC7' }} />

        {groupTabs.map((tab) => {
          const key = String(tab.key);
          const isAll = key === 'all';
          const isActive = isAll ? selectedGroups.length === 0 : selectedGroups.includes(key);

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => toggleGroup(key)}
              style={{
                height: 28,
                padding: '0 12px',
                borderRadius: 10,
                border: isActive ? '1px solid #0D47A1' : '1px solid #E5E7EB',
                background: isActive ? '#0D47A1' : '#FFFFFF',
                color: isActive ? '#FFFFFF' : '#111827',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              title={isAll ? 'Limpar filtro de grupos' : 'Alternar este grupo'}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div style={{ maxHeight: 460, overflowY: 'auto', paddingRight: 6 }}>
        {filteredModels.map((model) => (
          <button
            key={model.id}
            type="button"
            onClick={() => onSelectModel?.(model)}
            style={{
              width: '100%',
              textAlign: 'left',
              borderRadius: 14,
              border: '1px solid #E5E7EB',
              padding: 16,
              marginBottom: 14,
              background: '#FFFFFF',
              boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: '#EEF2F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <DescriptionOutlinedIcon style={{ fontSize: 18, color: '#6B7280' }} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 10,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Text type="regular" weight={700} color="dark">
                      {model.title}
                    </Text>
                  </div>

                {model.group ? (
                  <span
                    style={{
                      padding: '2px 10px',
                      borderRadius: 10,
                      border: '1px solid #93C5FD',
                      background: '#EFF6FF',
                      color: '#1D4ED8',
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                    title={model.group}
                  >
                    {model.group}
                  </span>
                ) : null}
                </div>

                <div style={{ marginTop: 3 }}>
                  <span
                    style={{
                      fontSize: 12,
                      color: '#9CA3AF',
                      fontWeight: 400,
                      lineHeight: '14px',
                      display: 'block',
                    }}
                  >
                    {model.description}
                  </span>
                </div>

                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    fontSize: 12,
                    color: '#B6BDC7',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FormatListBulletedIcon style={{ fontSize: 16, color: '#B6BDC7' }} />
                    <span style={{ fontWeight: 600, color: '#9CA3AF' }}>{model.items}</span>
                    <span>itens</span>
                  </span>

                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarTodayOutlinedIcon style={{ fontSize: 15, color: '#B6BDC7' }} />
                    <span style={{ fontWeight: 600, color: '#9CA3AF' }}>{model.createdAt}</span>
                  </span>
                </div>
              </div>

              <ChevronRightIcon style={{ fontSize: 22, color: '#C7CDD6' }} />
            </div>
          </button>
        ))}

        {filteredModels.length === 0 && (
          <Text type="regular" color="gray">
            Nenhum checklist encontrado para esse filtro.
          </Text>
        )}
      </div>
    </>
  );
}